import { Box, useMediaQuery } from "@mui/material";
import { getWeek } from "date-fns";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import { PaginationProvider } from "../../common/components/functional/pagination/PaginationProvider";
import { ENTRIES_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { getName } from "../../common/helpers/common";
import {
  getFiltersFromLocalStorage,
  mergeInitialAndStoredFilters,
} from "../../common/helpers/filters";
import isEmpty from "../../common/helpers/isEmpty";
import { Nullable } from "../../common/types/nullable";
import { useTablePagination } from "../../hooks/useTablePagination";
import EntriesFilterTabs from "./EntriesFilters/EntriesFilterTabs";
import EntriesFilters from "./EntriesFilters/EntriesFilters";
import {
  applyFilters,
  getDateRangeDefaultValues,
  getInitialFilters,
  withDefaultDateRangeIfEmpty,
  withTabDefiningFilters,
} from "./EntriesFilters/helpers";
import EntriesTable from "./EntriesTable/EntriesTable";
import NewEntryDialog from "./Entry/New/NewEntryDialog";
import {
  ENTRIES_DEFAULT_GROUPING,
  ENTRIES_FILTERS_ABSENCE_LOCAL_STORAGE,
  ENTRIES_FILTERS_CUSTOM_LOCAL_STORAGE,
  ENTRIES_FILTERS_GROUPING_LOCAL_STORAGE,
  ENTRIES_FILTERS_REJECTED_LOCAL_STORAGE,
  EntriesGroupAndSortKey,
  MILLISECONDS_IN_DAY_MINUS_ONE,
  MONTHLY_SALARY_EXPENSE_CODE,
} from "./constants";
import { EditEntry, Entry, Expense, NewEntryTalentData } from "./entriesAPI";
import {
  clearCursor,
  fetchEntries,
  fetchMoreEntries,
  selectAllEntries,
} from "./entriesSlice";
import { GroupKey } from "./types";

type Props = {
  newEntryTalentData?: NewEntryTalentData;
  isReadOnly: boolean;
};

type GroupedData = {
  [propertyValue: string]: GroupedData | Entry[];
};

export const getUniqueExpenses = (arr: Entry[]) => {
  let expenseTypeCodes: string[] = [];

  arr.forEach((item: Entry) => {
    const expenses = item.expenses;
    if (expenses) {
      Object.values(expenses).forEach((expense: Expense) => {
        expenseTypeCodes.push(expense.expenseTypeCode);
      });
    }
  });

  // Don't show monthly salary expenses (YLPE-7610)
  expenseTypeCodes = expenseTypeCodes
    .sort()
    .filter((value) => value !== MONTHLY_SALARY_EXPENSE_CODE);

  return [...new Set(expenseTypeCodes)];
};

export default function EntriesList({ newEntryTalentData, isReadOnly }: Props) {
  const talentId = newEntryTalentData?.id;

  const rejectedFromLocalStorage = getFiltersFromLocalStorage(
    ENTRIES_FILTERS_REJECTED_LOCAL_STORAGE,
  );
  const absenceFromLocalStorage = getFiltersFromLocalStorage(
    ENTRIES_FILTERS_ABSENCE_LOCAL_STORAGE,
  );
  const customFromLocalStorage = getFiltersFromLocalStorage(
    ENTRIES_FILTERS_CUSTOM_LOCAL_STORAGE,
  );
  const groupingFromLocalStorage = localStorage.getItem(
    ENTRIES_FILTERS_GROUPING_LOCAL_STORAGE,
  );

  const initialFilters = talentId
    ? getInitialFilters(talentId)
    : getInitialFilters();

  // Note: use stored filters only in main list view, not in talent profile
  const userFiltersRejected: SearchFilter[] = withDefaultDateRangeIfEmpty(
    mergeInitialAndStoredFilters(initialFilters, rejectedFromLocalStorage),
  );
  const userFiltersAbsence: SearchFilter[] = withDefaultDateRangeIfEmpty(
    mergeInitialAndStoredFilters(initialFilters, absenceFromLocalStorage),
  );
  const userFiltersCustom: SearchFilter[] = withDefaultDateRangeIfEmpty(
    mergeInitialAndStoredFilters(initialFilters, customFromLocalStorage),
  );
  const userFiltersGrouping: GroupKey[] =
    groupingFromLocalStorage && JSON.parse(groupingFromLocalStorage);

  const [filtersMenuOpen, setFiltersMenuOpen] = useState<boolean>(false);
  const [filterSearchString, setFilterSearchString] = useState<string>("");
  const [filters, setFilters] = useState<SearchFilter[]>(
    talentId || isEmpty(rejectedFromLocalStorage)
      ? initialFilters
      : withTabDefiningFilters(userFiltersRejected, 0),
  );
  const [filterTab, setFilterTab] = useState(talentId ? 2 : 0);

  // Note: if filters tab order changes, update this array
  const tabLocalStorageName: string = [
    ENTRIES_FILTERS_REJECTED_LOCAL_STORAGE,
    ENTRIES_FILTERS_ABSENCE_LOCAL_STORAGE,
    ENTRIES_FILTERS_CUSTOM_LOCAL_STORAGE,
  ][filterTab];

  const defaultFiltersForTab =
    !talentId && (filterTab === 0 || filterTab === 1)
      ? withTabDefiningFilters(initialFilters, filterTab)
      : undefined;

  const [groups, setGroups] = useState<GroupKey[]>(
    userFiltersGrouping || ENTRIES_DEFAULT_GROUPING,
  );
  const [selectedEntries, setSelectedEntries] = useState<Entry[]>([]);
  const [newEntry, setNewEntry] = useState<Nullable<EditEntry>>(null);
  const [groupedData, setGroupedData] = useState<any>({});
  const [newEntryDialogOpen, setNewEntryDialogOpen] = useState(false);
  const [newAbsenceDialogOpen, setNewAbsenceDialogOpen] = useState(false);

  const matches = useMediaQuery("(max-width:1920px)");

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const entries = useAppSelector((state) => selectAllEntries(state));
  const entriesState = useAppSelector((state) => state.entries);
  const cursor = entriesState.entries.cursor;
  const hits = entriesState.entries.hits;
  const isLoading = entriesState.entries.status === "pending";

  const translationNS = ["hourEntries", "common"];
  const pageSize = ENTRIES_PAGE_SIZE;

  // const supplementHeaders = getUniqueObjectKeys(entries, "supplements");

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<Entry>({
      data: entries,
      cursor,
      hits,
      pageSize: parseInt(pageSize, 10),
      fetchNext: (cursor) => fetchMoreEntries({ cursor, pageSize }),
    });

  const expensesHeaders = getUniqueExpenses(pageData);

  useEffect(() => {
    const appliedFilters = applyFilters(filters);
    // If filters have approval property with 'all' value,
    //remove property as in this case query should not have property at all
    let modifiedFilters = { ...appliedFilters };
    if (modifiedFilters?.approval === "all") {
      delete modifiedFilters.approval;
    }

    // Remove status modifier from API-request and add type

    if (modifiedFilters.status === "absence") {
      modifiedFilters.type = "Absence";
    } else if (modifiedFilters.status === "actualized") {
      modifiedFilters.type = "Work";
    }

    delete modifiedFilters.status;

    // API doesn't accept empty dates, so check if rangeStart and rangeEnd are provided in filters
    if (!modifiedFilters.rangeStart && !modifiedFilters.rangeEnd) {
      // If not provided, get the default date range
      const defaultDateRange = getDateRangeDefaultValues(new Date());
      modifiedFilters = {
        ...modifiedFilters,
        //rangeStart: defaultDateRange.startDate.getTime(),
        rangeEnd: defaultDateRange.endDate.getTime(),
      };
    }

    const setDateToEndOfDay = (date: number): number => {
      return date + MILLISECONDS_IN_DAY_MINUS_ONE;
    };

    modifiedFilters.rangeEnd = setDateToEndOfDay(modifiedFilters.rangeEnd);
    // TODO FIXME rangeEnd becomes NaN if filter is removed from UI
    // For now, check for that and delete nonlegit filter
    if (!modifiedFilters.rangeEnd) {
      delete modifiedFilters.rangeEnd;
    }

    // Sort parameter is grouping array as comma-separated list of 1-5 sortKeys “TALENT, TIME, CUSTOMER_ORG, CUSTOMER_CC, WORK_LOCATION”
    const getSortingParameter = (): string => {
      let sortKeysString;
      // Check if "TIME" is in the array
      // If it is not, add it to the end because we want the deepest grouping level to be sorted by time
      if (groups.includes(EntriesGroupAndSortKey.Time)) {
        sortKeysString = groups.join(",");
      } else {
        sortKeysString = [...groups, EntriesGroupAndSortKey.Time].join(",");
      }
      return sortKeysString;
    };

    dispatch(
      fetchEntries({
        ...(talentId && { talentId }),
        sort: getSortingParameter(),
        pageSize: ENTRIES_PAGE_SIZE,
        ...modifiedFilters,
      }),
    );
  }, [dispatch, filters, groups, talentId]);

  // TODO: can be removed after fetchEntries works on initial render
  useEffect(() => {
    return () => {
      dispatch(clearCursor());
    };
  }, [dispatch]);

  const nestGroupBy = React.useCallback(
    (arr: Entry[], properties: GroupKey[]): GroupedData => {
      if (properties.length === 1) {
        const property = properties[0];
        const groups = groupBy(arr, property);
        const result: GroupedData = {};
        for (const key in groups) {
          result[key] = groups[key];
        }
        return result;
      }
      const property = properties[0];
      const groups = groupBy(arr, property);
      const result: GroupedData = {};
      for (const key in groups) {
        result[key] = nestGroupBy(groups[key], properties.slice(1));
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const groupBy = (entries: Entry[], property: GroupKey) =>
    entries.reduce((acc: Record<string, Entry[]>, obj: Entry) => {
      let key: string | number = "";

      switch (property) {
        case EntriesGroupAndSortKey.Time:
          const week = getWeek(new Date(obj.start), { weekStartsOn: 1 });
          key = `${t(`${translationNS}:week`)} ${week}`;
          break;
        case EntriesGroupAndSortKey.Talent:
          key = getName(obj);
          break;
        case EntriesGroupAndSortKey.CustomerOrg:
          key = obj.customerOrgName;
          break;
        case EntriesGroupAndSortKey.CustomerCC:
          key = obj.customerCostCenterName;
          break;
        case EntriesGroupAndSortKey.WorkLocation:
          key = obj.workLocationName;
          break;
        default:
          key = obj[property];
      }

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(obj);
      return acc;
    }, {});

  useEffect(() => {
    if (pageData && !!groups.length) {
      const grouped = nestGroupBy(pageData, groups);
      setGroupedData({ ...grouped });
    }
  }, [groups, nestGroupBy, pageData]);

  const checkDefaultTabsAndFilters = (filters: any) => {
    const customerRejectedDefaultApprovalChanged = filters.some(
      (filter: any) =>
        filter.name === "approval" &&
        filter.value !== "CustomerRejected" &&
        filterTab === 0,
    );

    const absenceDefaultStatusChanged = filters.some(
      (filter: any) =>
        filter.name === "status" &&
        filter.value !== "absence" &&
        filterTab === 1,
    );

    const absenceDefaultApprovalChanged = filters.some(
      (filter: any) =>
        filter.name === "approval" && filter.value !== "New" && filterTab === 1,
    );
    return (
      customerRejectedDefaultApprovalChanged ||
      absenceDefaultStatusChanged ||
      absenceDefaultApprovalChanged
    );
  };

  const areAllFiltersEmpty = (filters: SearchFilter[]) => {
    return filters.every((filter) => !filter.value);
  };

  const updateFilters = (newFilters: SearchFilter[]) => {
    resetPagination();
    if (
      // If all filters are empty, change tab to 2 (customized)
      areAllFiltersEmpty(newFilters) ||
      // If at least one filter has a value, perform other checks:
      // If filter values are changed against rules of
      // CustomerRejected or Absence filterTab, change tab to 2 (customized)
      // Changing tab will cause default values to be added in EntriesFilters.tsx
      checkDefaultTabsAndFilters(newFilters)
    ) {
      setFilterTab(2);
    }
    setFilters([...newFilters]);
  };

  const handleGrouping = (value: GroupKey[]) => setGroups(value);

  const handleFiltering = (value: number) => {
    // Handle tab changing without filter-menu being open
    if (filtersMenuOpen) return;
    if (value === 0) {
      setFilters(
        !talentId && !isEmpty(rejectedFromLocalStorage)
          ? withTabDefiningFilters(userFiltersRejected, 0)
          : withTabDefiningFilters(initialFilters, 0),
      );
    } else if (value === 1) {
      setFilters(
        !talentId && !isEmpty(absenceFromLocalStorage)
          ? withTabDefiningFilters(userFiltersAbsence, 1)
          : withTabDefiningFilters(initialFilters, 1),
      );
    } else if (value === 2) {
      setFilters(
        !talentId && !isEmpty(customFromLocalStorage)
          ? userFiltersCustom
          : initialFilters,
      );
    }
  };

  // Save filters to local storage if user is not in talent profile
  useEffect(() => {
    if (talentId) return;
    if (filterTab === 0) {
      localStorage.setItem(
        ENTRIES_FILTERS_REJECTED_LOCAL_STORAGE,
        JSON.stringify(filters),
      );
    } else if (filterTab === 1) {
      localStorage.setItem(
        ENTRIES_FILTERS_ABSENCE_LOCAL_STORAGE,
        JSON.stringify(filters),
      );
    } else if (filterTab === 2) {
      localStorage.setItem(
        ENTRIES_FILTERS_CUSTOM_LOCAL_STORAGE,
        JSON.stringify(filters),
      );
    }
  }, [filters, filterTab, talentId]);

  // If grouping changes, update it to local storage
  useEffect(() => {
    localStorage.setItem(
      ENTRIES_FILTERS_GROUPING_LOCAL_STORAGE,
      JSON.stringify(groups),
    );
  }, [groups]);

  const handleFilterMenuOpenClose = (value: boolean | undefined) => {
    const newValue = typeof value === "boolean" ? value : !filtersMenuOpen;
    setFiltersMenuOpen(newValue);
  };

  const handleTabChangeByFilters = (newValue: string | [], key: string) => {
    const customerRejectedlDefaultApprovalChanged =
      key === "approval" && newValue !== "CustomerRejected" && filterTab === 0;

    const absenceDefaultStatusChanged =
      key === "status" && newValue !== "absence" && filterTab === 1;

    const absenceDefaultApprovalChanged =
      key === "approval" && newValue !== "New" && filterTab === 1;

    if (
      customerRejectedlDefaultApprovalChanged ||
      absenceDefaultStatusChanged ||
      absenceDefaultApprovalChanged
    ) {
      return setFilterTab(2);
    }
  };

  const toggleNewEntryDialogOpen = (open: boolean) =>
    setNewEntryDialogOpen(open);

  const toggleNewAbsenceDialogOpen = (open: boolean) =>
    setNewAbsenceDialogOpen(open);

  return (
    <Box sx={{ width: "100%", maxWidth: matches ? "1620px" : "1800px" }}>
      <OverlaySpinner isLoading={isLoading} />
      <FilterSearchField
        isOpen={filtersMenuOpen}
        toggleMenu={() => setFiltersMenuOpen(!filtersMenuOpen)}
        label="Label"
        value={filterSearchString}
        onChange={(value) => setFilterSearchString(value)}
        searchHistory={false}
        selectFromHistory={() => {}}
        translation={translationNS}
        filters={filters}
        updateFilters={updateFilters}
        width="980px"
        fieldComponent={
          <EntriesFilterTabs
            handleFiltering={handleFiltering}
            filterTab={filterTab}
            setFilterTab={setFilterTab}
            setSelectedEntries={setSelectedEntries}
            toggleNewEntryDialogOpen={toggleNewEntryDialogOpen}
            toggleNewAbsenceDialogOpen={toggleNewAbsenceDialogOpen}
            talentId={talentId}
            isReadOnly={isReadOnly}
          />
        }
        hideDeleteAllFilters={filterTab !== 2}
        defaultFilters={defaultFiltersForTab}
        filtersLocalStorageName={!talentId ? tabLocalStorageName : undefined}
      >
        <EntriesFilters
          groups={groups}
          filters={filters}
          updateFilters={updateFilters}
          handleMenuToggle={handleFilterMenuOpenClose}
          handleGrouping={handleGrouping}
          selectedEntries={selectedEntries}
          setSelectedEntries={setSelectedEntries}
          handleTabChangeByFilters={handleTabChangeByFilters}
        />
      </FilterSearchField>
      <PaginationProvider
        value={{
          pageData,
          count,
          onPageChange,
          pageIndex,
          pageSize: parseInt(pageSize, 10),
        }}
      >
        <EntriesTable
          groupedData={groupedData}
          groups={groups}
          supplementHeaders={[]}
          expensesHeaders={expensesHeaders}
          selectedEntries={selectedEntries as Entry[]}
          setSelectedEntries={setSelectedEntries}
          filterTab={filterTab}
          newEntry={newEntry}
          setNewEntry={setNewEntry}
          newAbsence={newAbsenceDialogOpen}
          setNewAbsence={setNewAbsenceDialogOpen}
          newEntryTalentData={newEntryTalentData}
        />
      </PaginationProvider>
      {talentId && newEntryDialogOpen && (
        <NewEntryDialog
          open={newEntryDialogOpen}
          toggleNewEntryDialogOpen={toggleNewEntryDialogOpen}
          talentId={talentId}
          setNewEntry={setNewEntry}
        />
      )}
    </Box>
  );
}
